import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { getDataThunkType } from '@europrocurement/l2d-redux-utils';
import { useApiRequestControllable } from '@europrocurement/l2d-modules';
import { useAchatsService, useSociete } from '@europrocurement/l2d-domain';
import { AppDispatch } from '../../../../../../redux';

import { reminderPublisherService } from '../services/reminderPublisherService';
import {
    SupplierSuggestion,
    SocietyEnumValue,
    ReminderCreationState,
} from '../types/reminderPublisherTypes';

/**
 * Interface pour les props du hook
 */
interface UseReminderPublisherCreationProps {
    onCreationSuccess?: () => void;
    onCreationError?: (error: Error) => void;
    fetchDataAfterSuccess?: getDataThunkType<unknown>;
}

/**
 * Hook pour gérer l'état et les actions de création des rappels fournisseurs
 */
export function useReminderPublisherCreation({
    onCreationSuccess,
    onCreationError,
    fetchDataAfterSuccess,
}: UseReminderPublisherCreationProps = {}) {
    const dispatch = useDispatch<AppDispatch>();
    const defaultSocietyId = useSociete();
    const { request: createRequest } = useApiRequestControllable();
    const { enqueueSnackbar } = useSnackbar();

    const { reminderPublisherApi } = useAchatsService();

    // État initial
    const initialState: ReminderCreationState = React.useMemo(
        () => ({
            currentStep: 'society',
            selectedSociety: null,
            suggestions: [],
            selectedSuggestions: [],
            customMessage: '',
            isMultiSocietyMode: false,
            isProcessing: false,
        }),
        [],
    );

    // État de la création de rappel
    const [state, setState] = React.useState<ReminderCreationState>(initialState);

    // Référence pour l'état actuel (utile pour les closures asynchrones)
    const stateRef = React.useRef(state);

    // Mettre à jour la référence quand l'état change
    React.useEffect(() => {
        stateRef.current = state;
    }, [state]);

    /**
     * Met à jour partiellement l'état
     */
    const updateState = React.useCallback((newState: Partial<ReminderCreationState>) => {
        setState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    }, []);

    /**
     * Réinitialise tout l'état à ses valeurs par défaut
     */
    const resetState = React.useCallback(() => {
        setState(initialState);
    }, [initialState]);

    /**
     * Met à jour la société sélectionnée
     */
    const handleSocietySelect = React.useCallback(
        (society: SocietyEnumValue | null) => {
            updateState({
                selectedSociety: society,
                isMultiSocietyMode: society === null,
            });
        },
        [updateState],
    );

    /**
     * Met à jour la liste complète des suggestions
     */
    const setSuggestions = React.useCallback(
        (newSuggestions: SupplierSuggestion[]) => {
            updateState({ suggestions: newSuggestions });
        },
        [updateState],
    );

    /**
     * Met à jour les suggestions sélectionnées
     */
    const setSelectedSuggestions = React.useCallback(
        (suggestions: SupplierSuggestion[]) => {
            updateState({ selectedSuggestions: suggestions });
        },
        [updateState],
    );

    /**
     * Met à jour le message personnalisé
     */
    const setCustomMessage = React.useCallback(
        (message: string) => {
            updateState({ customMessage: message });
        },
        [updateState],
    );

    /**
     * Crée les rappels fournisseurs à partir des suggestions sélectionnées
     */
    const createReminderPublishers = React.useCallback(
        async (finalMessage?: string) => {
            // Utiliser la référence pour avoir l'état le plus récent
            const { selectedSuggestions, customMessage, selectedSociety } = stateRef.current;

            if (selectedSuggestions.length === 0) {
                enqueueSnackbar('Aucun fournisseur sélectionné', { variant: 'warning' });
                return;
            }

            const message = finalMessage !== undefined ? finalMessage : customMessage;
            updateState({ isProcessing: true });

            try {
                // Créer des requêtes pour chaque rappel
                const creationPromises = selectedSuggestions.map((suggestion) =>
                    reminderPublisherService.createReminderPublisher(
                        createRequest,
                        suggestion,
                        message,
                        Number(defaultSocietyId),
                        reminderPublisherApi,
                        selectedSociety,
                    ),
                );

                // Exécuter toutes les requêtes
                await Promise.all(creationPromises);

                // Afficher un message de succès
                const count = selectedSuggestions.length;
                enqueueSnackbar(
                    `${count > 1 ? `${count} rappels fournisseurs créés` : 'Rappel fournisseur créé'} avec succès.`,
                    { variant: 'success' },
                );

                // Rafraîchir les données si nécessaire
                if (fetchDataAfterSuccess) {
                    dispatch(fetchDataAfterSuccess({}));
                }

                // Notifier le succès
                if (onCreationSuccess) {
                    onCreationSuccess();
                }

                resetState();
            } catch (error) {
                console.error('Erreur lors de la création des rappels:', error);

                enqueueSnackbar('Une erreur est survenue lors de la création des rappels.', {
                    variant: 'error',
                });

                if (onCreationError) {
                    onCreationError(error instanceof Error ? error : new Error('Erreur inconnue'));
                }

                updateState({ isProcessing: false });
            }
        },
        [
            createRequest,
            defaultSocietyId,
            dispatch,
            enqueueSnackbar,
            fetchDataAfterSuccess,
            onCreationError,
            onCreationSuccess,
            reminderPublisherApi,
            resetState,
            updateState,
        ],
    );

    return {
        state,
        handleSocietySelect,
        setSuggestions,
        setSelectedSuggestions,
        setCustomMessage,
        createReminderPublishers,
        resetState,
        updateState,
    };
}
